import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Check, AlertTriangle, RefreshCw, Zap } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { getSharePointSyncStatus, syncSharePointNow, upsertSharePointConfig } from "../lib/api";
import { logger } from "../lib/logger";

const UNIT_OPTIONS = [
  { key: "hours", key_i18n: "unit_hours", to_minutes: 60, min: 1, max: 168 },
  { key: "days", key_i18n: "unit_days", to_minutes: 60 * 24, min: 1, max: 60 },
  { key: "months", key_i18n: "unit_months", to_minutes: 60 * 24 * 30, min: 1, max: 12 },
];

/**
 * Split a minutes value into (amount, unit) picking the LARGEST unit that
 * divides cleanly. E.g. 4320 → (3, days), 60 → (1, hours), 43200 → (1, months).
 *
 * Falls back to hours when the value isn't cleanly divisible by any bigger
 * unit — better for custom schedules like 90min.
 */
function minutesToAmountUnit(mins) {
  const n = Math.max(1, Math.floor(mins || 60));
  // Try months, days, hours in order — first clean divisor wins.
  for (const u of [UNIT_OPTIONS[2], UNIT_OPTIONS[1], UNIT_OPTIONS[0]]) {
    if (n % u.to_minutes === 0) return { amount: Math.max(1, n / u.to_minutes), unit: u.key };
  }
  // Non-standard (e.g. 90min): coerce to hours rounded up
  return { amount: Math.max(1, Math.round(n / 60)), unit: "hours" };
}

function relative(t, iso) {
  if (!iso) return t("sync_never_ran");
  try {
    const d = new Date(iso);
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    const abs = Math.abs(diffSec);
    let value, unit;
    if (abs < 60) { value = abs; unit = "s"; }
    else if (abs < 3600) { value = Math.round(abs / 60); unit = "min"; }
    else if (abs < 86400) { value = Math.round(abs / 3600); unit = "h"; }
    else return d.toLocaleString();
    return diffSec < 0 ? t("time_in", { value, unit }) : t("time_ago", { value, unit });
  } catch {
    return iso;
  }
}

export default function LiveSyncCard({ projectId, isAdmin, libraryUrl, includeSubfolders, initialEnabled, initialIntervalMinutes, onChange }) {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState(!!initialEnabled);
  const [intervalMin, setIntervalMin] = useState(initialIntervalMinutes || 1440);
  const initialAU = minutesToAmountUnit(initialIntervalMinutes || 1440);
  const [amount, setAmount] = useState(initialAU.amount);
  const [unit, setUnit] = useState(initialAU.unit);
  const [savingCfg, setSavingCfg] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [, setNow] = useState(Date.now());

  // tick relative-time clock once per minute
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(id);
  }, []);

  const refreshStatus = useCallback(async () => {
    try { setStatus(await getSharePointSyncStatus(projectId)); }
    catch (err) {
      // Status check is best-effort; the user can still trigger sync manually.
      logger.debug("getSharePointSyncStatus failed:", err?.response?.status || err?.message);
    }
  }, [projectId]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // Poll while a sync is in progress
  useEffect(() => {
    if (!status?.sync_in_progress) return;
    const id = window.setInterval(refreshStatus, 3000);
    return () => window.clearInterval(id);
  }, [status?.sync_in_progress, refreshStatus]);

  const saveCfg = async (nextEnabled, nextInterval) => {
    if (!isAdmin) return;
    if (!libraryUrl) { toast.error("Salve o link da biblioteca primeiro."); return; }
    setSavingCfg(true);
    try {
      await upsertSharePointConfig(projectId, {
        library_url: libraryUrl,
        include_subfolders: !!includeSubfolders,
        auto_sync_enabled: !!nextEnabled,
        auto_sync_interval_minutes: nextInterval,
      });
      toast.success(nextEnabled ? "Auto-sync ativada" : "Auto-sync desativada");
      onChange?.({ auto_sync_enabled: !!nextEnabled, auto_sync_interval_minutes: nextInterval });
      await refreshStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      // revert UI
      setEnabled(!!initialEnabled);
      setIntervalMin(initialIntervalMinutes || 60);
    } finally { setSavingCfg(false); }
  };

  const onToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    await saveCfg(next, intervalMin);
  };
  const commitAmountUnit = async (nextAmount, nextUnit) => {
    const u = UNIT_OPTIONS.find((x) => x.key === nextUnit) || UNIT_OPTIONS[0];
    const clamped = Math.max(u.min, Math.min(u.max, Math.floor(nextAmount || 1)));
    setAmount(clamped);
    setUnit(nextUnit);
    const mins = clamped * u.to_minutes;
    setIntervalMin(mins);
    if (enabled) await saveCfg(true, mins);
  };
  const onSyncNow = async () => {
    setSyncing(true);
    try {
      const r = await syncSharePointNow(projectId);
      if (r.ok) {
        const changed = (r.new || 0) + (r.updated || 0);
        if (changed === 0) toast.success(t("sync_no_changes"));
        else toast.success(`${t("sync_success")} — ${r.new || 0} ${t("sync_files_new")} · ${r.updated || 0} ${t("sync_files_updated")}`);
      } else toast.error(`${t("sync_failed")}: ${r.error || ""}`);
    } catch (e) {
      if (e.response?.status === 409) toast.warning(t("sync_in_progress_msg"));
      else toast.error(e.response?.data?.detail || e.message);
    }
    finally { setSyncing(false); await refreshStatus(); }
  };

  const summary = status?.last_sync_summary;

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="livesync-card">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
        <span className="cs-label">step 4</span>
        <h4 className="font-heading text-sm font-semibold inline-flex items-center gap-1.5"><Zap size={14} className="text-cs-accent" />{t("step_autosync_title")}</h4>
      </div>
      <p className="text-xs text-zinc-600 mt-3">{t("autosync_subtitle")}</p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Toggle */}
        <label className={`relative flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${enabled ? "border-cs-accent bg-orange-50/40" : "border-zinc-200 bg-white hover:border-zinc-300"}`} data-testid="livesync-toggle-label">
          <input
            type="checkbox"
            className="sr-only"
            checked={!!enabled}
            disabled={!isAdmin || savingCfg}
            onChange={onToggle}
            data-testid="livesync-toggle-input"
          />
          <div className={`mt-0.5 w-9 h-5 rounded-full transition-colors ${enabled ? "bg-cs-accent" : "bg-zinc-300"}`}>
            <div className={`w-4 h-4 rounded-full bg-white shadow translate-y-0.5 transition-transform ${enabled ? "translate-x-[18px]" : "translate-x-0.5"}`} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-900">{t("autosync_enable")}</div>
            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{enabled ? t("sync_label_active") : t("sync_label_inactive")}</div>
          </div>
        </label>

        {/* Frequency selector — number + unit dropdown (iter42) */}
        <div className={`p-3 rounded-md border ${enabled ? "border-zinc-200" : "border-dashed border-zinc-200 opacity-60"}`} data-testid="livesync-frequency">
          <div className="cs-label !text-[10px] mb-1.5">{t("autosync_frequency")}</div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("every")}</span>
            <input
              type="number"
              min={1}
              step={1}
              value={amount}
              disabled={!isAdmin || !enabled || savingCfg}
              onChange={(e) => setAmount(parseInt(e.target.value, 10) || 1)}
              onBlur={(e) => commitAmountUnit(parseInt(e.target.value, 10) || 1, unit)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              className="w-16 px-2 py-1.5 border border-zinc-300 rounded text-xs disabled:bg-zinc-100"
              data-testid="livesync-freq-amount"
            />
            <select
              value={unit}
              disabled={!isAdmin || !enabled || savingCfg}
              onChange={(e) => commitAmountUnit(amount, e.target.value)}
              className="flex-1 px-2 py-1.5 border border-zinc-300 rounded text-xs disabled:bg-zinc-100"
              data-testid="livesync-freq-unit"
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u.key} value={u.key}>{t(u.key_i18n)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sync Now */}
        <div className="p-3 rounded-md border border-zinc-200" data-testid="livesync-now-cell">
          <div className="cs-label !text-[10px] mb-1.5">on-demand</div>
          <button
            onClick={onSyncNow}
            disabled={!isAdmin || syncing || status?.sync_in_progress}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-zinc-950 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-zinc-800 disabled:opacity-60"
            data-testid="livesync-sync-now-btn"
          >
            <RefreshCw size={12} className={syncing || status?.sync_in_progress ? "animate-spin" : ""} />
            {syncing || status?.sync_in_progress ? t("syncing_now") : t("sync_now_btn")}
          </button>
        </div>
      </div>

      {/* Status row */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="livesync-status">
        <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/50">
          <div className="cs-label !text-[10px] inline-flex items-center gap-1"><Activity size={11} /> {t("last_sync_at")}</div>
          <div className="font-mono text-xs text-zinc-900 mt-1">
            {status?.last_synced_at ? relative(t, status.last_synced_at) : t("sync_never_ran")}
          </div>
          {status?.last_synced_at && <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{new Date(status.last_synced_at).toLocaleString()}</div>}
        </div>
        <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/50">
          <div className="cs-label !text-[10px]">{t("next_sync_at")}</div>
          <div className="font-mono text-xs text-zinc-900 mt-1">
            {status?.auto_sync_enabled && status?.next_sync_at ? relative(t, status.next_sync_at) : "—"}
          </div>
          {status?.next_sync_at && <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{new Date(status.next_sync_at).toLocaleString()}</div>}
        </div>
      </div>

      {/* Summary / Error */}
      {summary && (
        <div className="mt-3 flex items-center gap-3 border border-zinc-200 rounded-md p-3 bg-white" data-testid="livesync-summary">
          <Check size={14} className="text-cs-success shrink-0" />
          <div className="text-xs text-zinc-800 font-mono">
            <span className="text-cs-success font-semibold">{summary.new || 0}</span> {t("sync_files_new")} ·{" "}
            <span className="text-cs-accent font-semibold">{summary.updated || 0}</span> {t("sync_files_updated")} ·{" "}
            <span className="text-zinc-500">{summary.skipped || 0}</span> {t("sync_files_skipped")} ·{" "}
            <span className={summary.failed ? "text-red-600 font-semibold" : "text-zinc-500"}>{summary.failed || 0}</span> {t("sync_files_failed")}
          </div>
        </div>
      )}
      {status?.last_sync_error && (
        <div className="mt-3 flex items-start gap-2 border border-red-200 bg-red-50 rounded-md p-3 text-xs text-red-700" data-testid="livesync-error">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div><div className="font-semibold uppercase tracking-wider text-[10px]">{t("last_sync_error")}</div><div className="mt-0.5">{status.last_sync_error}</div></div>
        </div>
      )}
    </div>
  );
}
